import { useState, useEffect, useRef } from 'react';
import { searchTracks, getCatalog } from '../../api/spotify.api';

const MAX_LOCAL_RESULTS = 50;

const normalize = (s) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

// Búsqueda local sobre la playlist VIP: todas las palabras deben aparecer en título o artista.
export function filterTracks(tracks, q) {
  const words = normalize(q).split(/\s+/).filter(Boolean);
  return tracks
    .filter((t) => {
      const haystack = normalize(`${t.name} ${t.artist}`);
      return words.every((w) => haystack.includes(w));
    })
    .slice(0, MAX_LOCAL_RESULTS);
}

// Vista previa de la playlist VIP para orientar a la mesa: artistas con más canciones (con la portada
// de su primera canción) y una muestra al azar de canciones.
export function catalogPreview(tracks, { artists: artistsMax = 12, songs: songsMax = 8, random = Math.random } = {}) {
  const byArtist = new Map();
  for (const t of tracks) {
    const a = byArtist.get(t.artist) ?? { name: t.artist, count: 0, image: t.image ?? null };
    a.count += 1;
    a.image ??= t.image ?? null;
    byArtist.set(t.artist, a);
  }
  const artists = [...byArtist.values()].sort((x, y) => y.count - x.count).slice(0, artistsMax);

  const pool = [...tracks];
  for (let i = pool.length - 1; i > 0; i--) { // Fisher–Yates
    const j = Math.floor(random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return { total: tracks.length, artists, songs: pool.slice(0, songsMax) };
}

// Modal de bienvenida en modo playlist: sale en cada escaneo (el QR abre pestaña nueva) pero no al recargar.
// sessionStorage y no localStorage: el QR principal devuelve la misma sala al mismo dispositivo, y con
// localStorage el modal no volvía a salir nunca.
export function usePlaylistIntro(roomId, mode) {
  const key = `queuebar:intro:${roomId}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
  });
  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(key, '1'); } catch { /* sin storage: solo se cierra */ }
  };
  return { show: mode === 'playlist' && !dismissed, dismiss };
}

// mode 'playlist': la sala solo acepta canciones de la playlist del local; se descarga una vez
// y se busca en memoria (sin llamadas a Spotify). Cualquier otro modo busca en Spotify.
export function useSearch(roomId, mode) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [catalogTracks, setCatalogTracks] = useState([]); // para la vista previa (catalogPreview)
  const catalog = useRef(null); // promesa del catálogo, compartida por todas las búsquedas

  useEffect(() => {
    if (mode !== 'playlist') return;
    catalog.current = getCatalog(roomId);
    catalog.current.then((c) => setCatalogTracks(c.tracks)).catch(() => {});
  }, [roomId, mode]);

  const search = async (q) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      if (mode === 'playlist') {
        catalog.current ??= getCatalog(roomId);
        // Si la descarga falló, se reintenta en la próxima búsqueda.
        const { tracks } = await catalog.current.catch((err) => { catalog.current = null; throw err; });
        setResults(filterTracks(tracks, q));
      } else {
        setResults(await searchTracks(roomId, q));
      }
    } catch (err) {
      setError(err.response?.data?.message ?? (err.response ? 'Error al buscar' : 'La búsqueda tardó demasiado. Intenta de nuevo.'));
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => { setResults([]); setError(null); setHasSearched(false); };

  return { results, loading, error, hasSearched, catalogTracks, search, clear };
}
